import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Card, IconButton, Button } from '@mui/material';
import ArgonBox from 'components/ArgonBox';
import PropTypes from 'prop-types';
import ArgonTypography from 'components/ArgonTypography';
import StageProductTable from '../tabels/StageProductTable';
import DeployProductTable from 'layouts/deploy/tabels/DeployProductTable';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import ArgonInput from 'components/ArgonInput';
import ArgonSelect from 'components/ArgonSelect';
import ClearIcon from '@mui/icons-material/Clear';

import { areProductsEqual } from '../utils/productDataHelper';

function StageProduct({
  variants,
  itemsPerPage = 10,
  selectedVariants,
  initialSelected,
  majorVersionProducts,
  parentDatabaseProducts,
  versionType,
  onMajorVersionsSelected,
  initialMajorVersions,
}) {
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [renderProducts, setRenderProducts] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredVariants, setFilteredVariants] = useState(variants || []);
  const [localSelectedVariants, setLocalSelectedVariants] = useState(initialSelected || []);
  const [tempVersionChanges, setTempVersionChanges] = useState({});
  const [localSelectedMajorVersions, setLocalSelectedMajorVersions] = useState([]);

  const TableComponent = useMemo(() => {
    return versionType === 'patch' ? DeployProductTable : StageProductTable;
  }, [versionType]);

  useEffect(() => {
    if (TableComponent === DeployProductTable) {
      setColumns([
        { name: 'product', align: 'left' },
        { name: 'select_version', align: 'center' },
      ]);
    } else {
      setColumns([
        { name: 'product', align: 'left' },
        { name: 'version', align: 'center' },
      ]);
    }
  }, [TableComponent]);

  useEffect(() => {
    if (versionType === 'patch') {
      if (majorVersionProducts) {
        const mergedVersions = majorVersionProducts.map((p) => {
          const initialSelection = initialMajorVersions?.find((init) => areProductsEqual(init, p));
          return {
            ...p,
            selected_version: initialSelection?.selected_version,
          };
        });
        setLocalSelectedMajorVersions(mergedVersions);
      }
    } else {
      setLocalSelectedMajorVersions([]);
    }
  }, [majorVersionProducts, versionType]);

  // --- REFACTORED: handleVersionChange now uses the product object and areProductsEqual ---
  const handleVersionChange = useCallback(
    (event, productToUpdate) => {
      const selectedVersion = event.target.value;
      const productId = productToUpdate.external_id || productToUpdate.id || productToUpdate.sf_id;
      const newTempChanges = { ...tempVersionChanges, [productId]: selectedVersion };
      setTempVersionChanges(newTempChanges);

      const isAlreadySelected = localSelectedVariants.some((p) => areProductsEqual(p, productToUpdate));

      if (isAlreadySelected) {
        onMajorVersionsSelected((prevSelected) =>
          prevSelected.map((product) => {
            if (areProductsEqual(product, productToUpdate)) {
              return { ...product, selected_version: selectedVersion };
            }
            return product;
          })
        );
      }

      setLocalSelectedMajorVersions((prevSelected) => {
        return prevSelected.map((product) => {
          if (areProductsEqual(product, productToUpdate)) {
            return { ...product, selected_version: selectedVersion };
          }
          return product;
        });
      });
    },
    [tempVersionChanges, localSelectedVariants, onMajorVersionsSelected]
  );

  const handleRowClick = useCallback(
    (event, product) => {
      const isSelected = localSelectedVariants.some((variant) => areProductsEqual(variant, product));

      // --- DESELECTION LOGIC ---
      // Deselection is always allowed and does not affect the chosen version.
      if (isSelected) {
        const updatedSelectedVariants = localSelectedVariants.filter((p) => !areProductsEqual(p, product));
        setLocalSelectedVariants(updatedSelectedVariants);
        selectedVariants(updatedSelectedVariants);

        if (versionType === 'patch') {
          const updatedMajorVersions = initialMajorVersions.filter((p) => !areProductsEqual(p, product));
          onMajorVersionsSelected(updatedMajorVersions);
        }
        return; // Exit after handling deselection.
      }

      // --- SELECTION LOGIC ---
      // If we are here, the user is trying to select a new row.

      // **THE GATEKEEPER:** For patch versions, block selection if no version is chosen.
      if (versionType === 'patch') {
        const productId = product.external_id || product.id || product.sf_id;
        const selectedVersionString = tempVersionChanges[productId];
        if (!selectedVersionString) {
          // A version has NOT been selected from the dropdown. Block the action.
          console.warn('Selection blocked: A version must be selected before the product can be added.');
          return; // Stop further execution.
        }
      }

      // If we passed the gatekeeper (or it's not a patch type), proceed with selection.
      const updatedSelectedVariants = [...localSelectedVariants, product];
      setLocalSelectedVariants(updatedSelectedVariants);
      selectedVariants(updatedSelectedVariants);

      if (versionType === 'patch') {
        // We are guaranteed to have a version string here because of the gatekeeper.
        const productId = product.external_id || product.id || product.sf_id;
        const selectedVersionString = tempVersionChanges[productId];
        const majorProductToAdd = majorVersionProducts.find(
          (major) => areProductsEqual(major, product) && major.product_version === selectedVersionString
        );

        if (majorProductToAdd) {
          const productToSend = { ...majorProductToAdd, selected_version: selectedVersionString };
          const updatedMajorVersions = [...initialMajorVersions, productToSend];
          onMajorVersionsSelected(updatedMajorVersions);
        }
      }
    },
    [
      localSelectedVariants,
      initialMajorVersions,
      tempVersionChanges,
      majorVersionProducts,
      versionType,
      selectedVariants,
      onMajorVersionsSelected,
    ]
  );

  const isRowSelected = (product) => {
    return localSelectedVariants.some((variant) => areProductsEqual(variant, product));
  };

  const handlePageChange = (newPage) => setCurrentPage(newPage);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    const filtered = variants.filter((variant) =>
      variant.name.toLowerCase().includes(event.target.value.toLowerCase())
    );
    setFilteredVariants(filtered);
    setCurrentPage(1);
  };

  const handleClearSelections = () => {
    setLocalSelectedVariants([]);
    setLocalSelectedMajorVersions([]);
    selectedVariants([]);
    onMajorVersionsSelected([]);
    setTempVersionChanges({});
  };

  useEffect(() => {
    const fetchData = () => {
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedVariants = filteredVariants?.slice(startIndex, endIndex);
      let formattedRows = [];

      if (TableComponent === DeployProductTable) {
        const groupedProducts = paginatedVariants.reduce((acc, product) => {
          const productExternalId = product.external_id || product.id || product.sfId || product.sf_id;
          if (!acc[productExternalId]) acc[productExternalId] = [];
          acc[productExternalId].push(product);
          return acc;
        }, {});

        formattedRows = Object.entries(groupedProducts)
          .map(([externalId, products]) => {
            if (!products || products.length === 0) return null;
            const representativeProduct = products[0];
            const versions = [
              ...new Set(
                majorVersionProducts
                  ?.filter((major) => areProductsEqual(major, representativeProduct))
                  .map((p) => p.product_version)
              ),
            ];
            const selectedMajor = localSelectedMajorVersions.find((major) =>
              areProductsEqual(major, representativeProduct)
            );

            const hasVersionSelected = !!tempVersionChanges[externalId];
            const isSelectable = hasVersionSelected || isRowSelected(representativeProduct);

            const initialValue =
              selectedMajor?.selected_version || tempVersionChanges[externalId] || products[0].product_version || '';
            const selectOptions = versions.map((version) => ({ value: version, label: version }));

            return {
              product: (
                <ArgonTypography variant="caption" fontWeight="bold">
                  {representativeProduct.name || 'Unknown Product'}
                </ArgonTypography>
              ),
              select_version: (
                <ArgonSelect
                  placeholder="Select Version"
                  value={initialValue}
                  options={selectOptions}
                  onChange={(e) => handleVersionChange(e, representativeProduct)}
                  onClick={(event) => event.stopPropagation()}
                />
              ),
              id: externalId,
              onClick: (event) => handleRowClick(event, representativeProduct),
              selected: isRowSelected(representativeProduct),
              // --- ADD THIS STYLE PROP ---
              style: { cursor: isSelectable ? 'pointer' : 'not-allowed' },
              isselectable: isSelectable,
            };
          })
          .filter(Boolean);
      } else {
        formattedRows = paginatedVariants?.map((product) => {
          const isSelected = isRowSelected(product);
          const dbProduct = parentDatabaseProducts.find((dbProd) => areProductsEqual(dbProd, product));
          const dbVersion = dbProduct ? dbProduct.product_version : '-';

          return {
            product: (
              <ArgonTypography variant="caption" fontWeight="bold">
                {product.name}
              </ArgonTypography>
            ),
            version: (
              <ArgonTypography variant="caption" fontWeight="bold">
                {dbVersion}
              </ArgonTypography>
            ),
            id: product.id,
            onClick: (event) => handleRowClick(event, product),
            selected: isSelected,
          };
        });
      }

      setRows(formattedRows);
      setRenderProducts(true);
    };

    fetchData();
  }, [
    filteredVariants,
    localSelectedVariants,
    currentPage,
    itemsPerPage,
    majorVersionProducts,
    versionType,
    TableComponent,
    handleVersionChange,
    tempVersionChanges,
    parentDatabaseProducts,
    variants,
    handleRowClick,
    localSelectedMajorVersions,
  ]);

  useEffect(() => {
    selectedVariants(localSelectedVariants);
  }, [localSelectedVariants, selectedVariants]);

  useEffect(() => {
    setFilteredVariants(variants || []);
  }, [variants]);

  const totalPages =
    Array.isArray(filteredVariants) && filteredVariants.length > 0
      ? Math.ceil(filteredVariants.length / itemsPerPage)
      : 1;

  return (
    <ArgonBox py={3}>
      <ArgonBox display="flex" alignItems="center" mb={2}>
        <ArgonInput
          type="text"
          placeholder="Search by name"
          value={searchTerm}
          onChange={handleSearchChange}
          style={{ width: 'calc(100% - 120px)', padding: '10px', marginRight: '10px' }}
        />
        <Button
          sx={{ width: '140px' }}
          variant="contained"
          color="secondary"
          onClick={handleClearSelections}
          startIcon={<ClearIcon />}
          style={{ height: '40px', backgroundColor: '#f0f2f5' }}
        >
          Clear ({localSelectedVariants?.length || 0})
        </Button>
      </ArgonBox>
      <ArgonBox mb={3}>
        <Card>
          <ArgonBox
            sx={{
              '& .MuiTableRow-root:not(:last-child)': {
                '& td': {
                  borderBottom: ({ borders: { borderWidth, borderColor } }) => `${borderWidth[1]} solid ${borderColor}`,
                },
              },
            }}
          >
            {renderProducts && <TableComponent rows={rows} columns={columns} />}
          </ArgonBox>
          <ArgonBox display="flex" justifyContent="center" mt={2} gap={2}>
            <IconButton onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} size="small">
              <ArrowLeftIcon />
            </IconButton>
            <ArgonTypography variant="body2" color="text">
              {currentPage} / {totalPages}
            </ArgonTypography>
            <IconButton
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              size="small"
            >
              <ArrowRightIcon />
            </IconButton>
          </ArgonBox>
        </Card>
      </ArgonBox>
    </ArgonBox>
  );
}

// PropTypes remain the same
StageProduct.propTypes = {
  variants: PropTypes.array.isRequired,
  itemsPerPage: PropTypes.number,
  selectedVariants: PropTypes.func.isRequired,
  initialSelected: PropTypes.array,
  majorVersionProducts: PropTypes.arrayOf(
    PropTypes.shape({
      product_version: PropTypes.string.isRequired,
      external_id: PropTypes.string,
      sf_id: PropTypes.string,
    })
  ).isRequired,
  onMajorVersionsSelected: PropTypes.func.isRequired,
  parentDatabaseProducts: PropTypes.arrayOf(
    PropTypes.shape({
      product_version: PropTypes.string,
      sf_id: PropTypes.string,
      id: PropTypes.string,
      external_id: PropTypes.string,
    })
  ),
  versionType: PropTypes.string.isRequired,
  initialMajorVersions: PropTypes.arrayOf(
    PropTypes.shape({
      product_version: PropTypes.string.isRequired,
      external_id: PropTypes.string,
      sf_id: PropTypes.string,
      selected_version: PropTypes.string,
    })
  ),
};

export default StageProduct;
